import { useState, useEffect } from "react";
import { DocumentLogEntry } from "@stratify/shared";
import { subscribeToDocumentLogs } from "../api/documentLogsApi";

interface UseDocumentLogsResult {
  logs: DocumentLogEntry[];
  loading: boolean;
  error: string | null;
}

/**
 * Hook to manage document logs state and subscription
 *
 * @param documentId - The ID of the document to fetch logs for
 * @returns Object containing:
 *  - logs: Array of document log entries
 *  - loading: Boolean indicating if logs are being fetched
 *  - error: Error message if fetching failed, null otherwise
 *
 * @example
 * ```tsx
 * const { logs, loading, error } = useDocumentLogs(documentId);
 *
 * if (loading) return <Loading />;
 * if (error) return <Error message={error} />;
 * return <LogsList logs={logs} />;
 * ```
 */
export const useDocumentLogs = (documentId: string): UseDocumentLogsResult => {
  const [logs, setLogs] = useState<DocumentLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Reset state when documentId changes
    setLoading(true);
    setError(null);

    // Subscribe to document logs
    const unsubscribe = subscribeToDocumentLogs(
      documentId,
      (logEntries) => {
        setLogs(logEntries);
        setLoading(false);
      },
      (_err) => {
        setError("Failed to load document logs");
        setLoading(false);
      }
    );

    // Cleanup subscription on unmount or when documentId changes
    return () => unsubscribe();
  }, [documentId]);

  return { logs, loading, error };
};
